import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const EMPTY = { full_name: "", company_name: "", email: "", whatsapp_number: "" };
const EMAIL_RX = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const MOBILE_RX = /^[6-9]\d{9}$/;

export default function ContactSalesModal({ open, onOpenChange }) {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const close = (v) => {
    onOpenChange(v);
    if (!v) setTimeout(() => { setForm(EMPTY); setErrors({}); setServerError(""); setSuccess(null); }, 250);
  };

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = "Please enter your name.";
    if (!form.company_name.trim()) e.company_name = "Please enter your company name.";
    if (!EMAIL_RX.test(form.email.trim())) e.email = "Please enter a valid work email address.";
    if (!MOBILE_RX.test(form.whatsapp_number.trim())) e.whatsapp_number = "Please enter a valid WhatsApp number.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    setServerError("");
    if (!validate()) return;
    setLoading(true);
    try {
      const r = await api.post("/public/sales-request", {
        full_name: form.full_name.trim(),
        company_name: form.company_name.trim(),
        email: form.email.trim(),
        whatsapp_number: `+91${form.whatsapp_number.trim()}`,
      });
      setSuccess(r.data.request);
    } catch (err) {
      setServerError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="gap-0 overflow-hidden bg-white p-0 sm:max-w-3xl" data-testid="contact-sales-modal">
        <div className="grid md:grid-cols-2">
          <div className="hidden flex-col justify-between p-8 md:flex" style={{ backgroundColor: "#0A1F4B" }}>
            <div>
              <span className="inline-block rounded-lg bg-white px-3 py-1.5">
                <img src="/brand/procdeal-logo.png" alt="ProcDeal" className="h-7 w-auto" />
              </span>
              <span className="mt-8 inline-block rounded-full border border-blue-400/40 bg-blue-400/10 px-3 py-1 text-[11px] font-semibold tracking-wide text-blue-300">
                CUSTOM ENTERPRISE PLAN
              </span>
              <h2 className="mt-3 font-heading text-2xl font-semibold text-white">Talk to Our Sales Team</h2>
              <p className="mt-3 text-sm leading-relaxed text-slate-300">
                Need more Buyers, Suppliers, Technical Reviewers, Approvers, integrations or custom workflows? Talk to our sales team.
              </p>
            </div>
            <p className="font-heading text-sm font-semibold text-blue-300">Connect. Compare. Close.</p>
          </div>

          <div className="p-6 sm:p-8">
            {success ? (
              <div className="flex h-full flex-col items-center justify-center text-center" data-testid="sales-success">
                <CheckCircle2 className="h-12 w-12 text-[#22C55E]" />
                <DialogTitle className="mt-4 font-heading text-xl font-semibold tracking-tight">Sales Request Received</DialogTitle>
                <DialogDescription className="mt-2 text-sm font-medium text-slate-700">Thank you for your interest in ProcDeal.</DialogDescription>
                <p className="mt-1 text-sm text-muted-foreground">
                  Our sales team will review your request and get in touch with you shortly.
                </p>
                <div className="mt-5 w-full rounded-lg border bg-slate-50 p-4 text-left text-sm">
                  <div className="grid grid-cols-3 gap-y-2">
                    <span className="text-xs uppercase tracking-wide text-muted-foreground">Name</span>
                    <span className="col-span-2 font-medium" data-testid="sales-success-name">{success.full_name}</span>
                    <span className="text-xs uppercase tracking-wide text-muted-foreground">Company</span>
                    <span className="col-span-2 font-medium" data-testid="sales-success-company">{success.company_name}</span>
                    <span className="text-xs uppercase tracking-wide text-muted-foreground">Email</span>
                    <span className="col-span-2 font-medium" data-testid="sales-success-email">{success.email}</span>
                    <span className="text-xs uppercase tracking-wide text-muted-foreground">WhatsApp</span>
                    <span className="col-span-2 font-medium" data-testid="sales-success-whatsapp">{success.whatsapp_number}</span>
                  </div>
                </div>
                <Button data-testid="sales-done-btn" className="mt-6 w-full bg-slate-900 hover:bg-slate-800" onClick={() => close(false)}>Done</Button>
                <button type="button" data-testid="sales-back-link" onClick={() => close(false)}
                  className="mt-3 text-sm font-medium text-blue-700 hover:underline">Back to ProcDeal</button>
              </div>
            ) : (
              <>
                <div className="mb-5 md:hidden">
                  <img src="/brand/procdeal-logo.png" alt="ProcDeal" className="h-7 w-auto" />
                  <span className="mt-3 inline-block rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-[11px] font-semibold tracking-wide text-blue-700">
                    CUSTOM ENTERPRISE PLAN
                  </span>
                </div>
                <DialogTitle className="font-heading text-xl font-semibold tracking-tight">Talk to Our Sales Team</DialogTitle>
                <DialogDescription className="mt-1 text-sm text-muted-foreground">
                  Tell us a little about your organization and our team will help design the right ProcDeal plan for you.
                </DialogDescription>
                <form onSubmit={submit} className="mt-5 space-y-4" noValidate data-testid="sales-form">
                  <div>
                    <Label htmlFor="sales-fn">Full Name *</Label>
                    <Input id="sales-fn" data-testid="sales-full-name" value={form.full_name} placeholder="Enter your full name"
                      onChange={(e) => set("full_name", e.target.value)} className={`mt-1.5 ${errors.full_name ? "border-red-400" : ""}`} />
                    {errors.full_name && <p data-testid="sales-error-full-name" className="mt-1 text-xs text-red-600">{errors.full_name}</p>}
                  </div>
                  <div>
                    <Label htmlFor="sales-cn">Company Name *</Label>
                    <Input id="sales-cn" data-testid="sales-company-name" value={form.company_name} placeholder="Enter your company name"
                      onChange={(e) => set("company_name", e.target.value)} className={`mt-1.5 ${errors.company_name ? "border-red-400" : ""}`} />
                    {errors.company_name && <p data-testid="sales-error-company-name" className="mt-1 text-xs text-red-600">{errors.company_name}</p>}
                  </div>
                  <div>
                    <Label htmlFor="sales-em">Work Email ID *</Label>
                    <Input id="sales-em" data-testid="sales-email" type="email" value={form.email} placeholder="you@company.com"
                      onChange={(e) => set("email", e.target.value)} className={`mt-1.5 ${errors.email ? "border-red-400" : ""}`} />
                    {errors.email && <p data-testid="sales-error-email" className="mt-1 text-xs text-red-600">{errors.email}</p>}
                  </div>
                  <div>
                    <Label htmlFor="sales-wa">WhatsApp Number *</Label>
                    <div className={`mt-1.5 flex rounded-md border shadow-sm ${errors.whatsapp_number ? "border-red-400" : "border-input"}`}>
                      <span className="flex items-center rounded-l-md border-r bg-slate-50 px-3 text-sm font-medium text-slate-600">+91</span>
                      <input id="sales-wa" data-testid="sales-whatsapp" type="tel" inputMode="numeric" maxLength={10}
                        value={form.whatsapp_number} placeholder="Enter your WhatsApp number"
                        onChange={(e) => set("whatsapp_number", e.target.value.replace(/\D/g, "").slice(0, 10))}
                        className="w-full rounded-r-md bg-transparent px-3 py-2 text-sm focus-visible:outline-none" />
                    </div>
                    {errors.whatsapp_number && <p data-testid="sales-error-whatsapp" className="mt-1 text-xs text-red-600">{errors.whatsapp_number}</p>}
                  </div>
                  {serverError && <p data-testid="sales-server-error" role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{serverError}</p>}
                  <Button type="submit" data-testid="sales-submit" disabled={loading} className="w-full bg-slate-900 hover:bg-slate-800">
                    {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Contact Sales
                  </Button>
                  <p className="text-center text-xs text-muted-foreground">
                    By submitting this form, you agree to be contacted by the ProcDeal team regarding your enquiry.
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
